import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageTour from "@/components/PageTour";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IbanField } from "@/components/IbanField";
import { isValidIban } from "@/lib/iban";
import { SmsCreditiSection } from "@/pages/CreditiSms";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Coins, Loader2, HandCoins, Wallet, BadgeCheck, Info, Crown,
  Building2, ArrowRight, Sparkles, Settings2, Search, Mail, CheckCircle2, ShieldAlert,
} from "lucide-react";

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const coins = (n) => `${(Number(n) || 0).toLocaleString("it-IT")} SC`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

const STATUS_META = {
  ACCRUED: { label: "Maturato", tone: "bg-amber-100 text-amber-700" },
  AVAILABLE: { label: "Disponibile", tone: "bg-emerald-100 text-emerald-700" },
  PAYMENT_REQUESTED: { label: "In liquidazione", tone: "bg-sky-100 text-sky-700" },
  INVOICED: { label: "Fatturato", tone: "bg-sky-100 text-sky-700" },
  APPROVED: { label: "Approvato", tone: "bg-sky-100 text-sky-700" },
  PAID: { label: "Liquidato", tone: "bg-blue-100 text-blue-700" },
  SPENT: { label: "Speso", tone: "bg-rose-100 text-rose-700" },
  SETTLED: { label: "Liquidato", tone: "bg-blue-100 text-blue-700" },
  REVERSED: { label: "Stornato", tone: "bg-rose-100 text-rose-700" },
};

const ONBOARDING_LABEL = { essenziale: "Essenziale", premium: "Premium", elite: "Elite" };

const EMPTY_ACCOUNT = {
  subject_type: "societa", name: "", referente: "", codice_fiscale: "", partita_iva: "",
  pec: "", email: "", telefono: "", sdi: "", indirizzo: "", citta: "", cap: "", provincia: "",
  iban: "", regime_fiscale: "", dati_ritenuta: "", accept_contract: false, email_verified: false,
};

const REQUIRED_JOIN_FIELDS = ["name", "referente", "partita_iva", "indirizzo", "citta", "cap", "provincia", "pec", "email", "telefono", "iban"];

const PARTNER_CONTRACT = `CONTRATTO SEDIO PARTNER — CORRISPETTIVI PER ONBOARDING

Fornitore: [Ragione sociale] — P.IVA [__________] — sede legale in [indirizzo completo] — PEC [__________] (di seguito "il Fornitore" o "Sedio").

Il presente contratto disciplina il rapporto di collaborazione "Sedio Partner" tra l'Amministratore (di seguito "il Partner"), che opera nell'ambito della propria attività professionale, e il Fornitore.

1. OGGETTO
Il Partner svolge attività di onboarding dei condomìni sulla piattaforma Sedio (creazione del condominio, attivazione dell'abbonamento e inserimento dell'anagrafica). A fronte della prestazione completata, il Fornitore riconosce un corrispettivo in SedioCoin secondo il Tariffario Partner vigente.

2. NATURA DEL CORRISPETTIVO
Il corrispettivo NON è un cashback e NON è calcolato in percentuale sul prezzo dell'abbonamento. Dipende esclusivamente dal tipo di onboarding completato (piano Essenziale, Premium o Elite) e dal Tariffario Partner attivo al momento della maturazione. 1 SedioCoin = 1 Euro (valore configurabile).

3. MATURAZIONE E DISPONIBILITÀ
Il corrispettivo matura al completamento dell'onboarding e diventa liquidabile secondo il criterio indicato in piattaforma (di norma alla conferma del primo pagamento del condominio). Ogni onboarding genera un solo corrispettivo per condominio (identificato dal codice fiscale).

4. LIQUIDAZIONE
La liquidazione è richiedibile al raggiungimento della soglia minima indicata in piattaforma. Il pagamento avviene tramite bonifico sui dati fiscali/bancari forniti dal Partner, previa emissione della documentazione fiscale eventualmente dovuta.

5. DATI FISCALI
Il Partner è responsabile della correttezza dei dati fiscali e bancari inseriti e degli adempimenti fiscali a proprio carico relativi ai corrispettivi percepiti.

6. NATURA DEL RAPPORTO
Il programma Partner non instaura alcun rapporto di lavoro subordinato, di agenzia o di mandato con rappresentanza. Il Partner opera in piena autonomia e senza vincolo di esclusiva; nulla è dovuto per attività non completate.

7. DURATA, RECESSO E MODIFICHE DEL TARIFFARIO
Il programma ha durata indeterminata. Ciascuna parte può recedere in qualsiasi momento con comunicazione scritta; i corrispettivi già maturati alla data del recesso restano liquidabili. Il Fornitore può aggiornare il Tariffario Partner con preavviso: al singolo corrispettivo si applica il tariffario vigente al momento della maturazione.

8. TRATTAMENTO DEI DATI
I dati sono trattati nel rispetto del Reg. UE 2016/679 (GDPR) ai soli fini della gestione del programma Partner e della liquidazione.

9. LEGGE APPLICABILE E FORO COMPETENTE
Il presente contratto è regolato dalla legge italiana. Per ogni controversia è competente in via esclusiva il Foro di [città sede del Fornitore].

10. ACCETTAZIONE
La spunta di accettazione costituisce adesione integrale alle presenti condizioni. Ai sensi degli artt. 1341-1342 c.c., il Partner approva specificamente le clausole 2 (natura del corrispettivo), 6 (natura del rapporto), 7 (recesso e modifiche del Tariffario) e 9 (foro competente).`;

/* ---------------- Adesione Partner ---------------- */
const JoinDialog = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [busy, setBusy] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_ACCOUNT, ...(initial || {}), accept_contract: false, email_verified: !!initial?.email_verified });
    setAtEnd(false); setCodeSent(false); setCode("");
    // Se non ho ancora aderito, precompilo dai dati del profilo studio (stessi campi obbligatori).
    if (!initial?.name) {
      api.get("/studio-profile").then((r) => {
        const p = r.data?.profile || {};
        setForm((f) => ({
          ...f,
          name: f.name || p.ragione_sociale || "",
          referente: f.referente || p.referente || "",
          partita_iva: f.partita_iva || p.partita_iva || "",
          codice_fiscale: f.codice_fiscale || p.codice_fiscale || "",
          sdi: f.sdi || p.sdi || "",
          indirizzo: f.indirizzo || p.indirizzo || "",
          citta: f.citta || p.citta || "",
          cap: f.cap || p.cap || "",
          provincia: f.provincia || p.provincia || "",
          pec: f.pec || p.pec || "",
          email: f.email || p.email || "",
          telefono: f.telefono || p.telefono || "",
        }));
      }).catch(() => {});
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onEmailChange = (e) => { setCodeSent(false); setForm((f) => ({ ...f, email: e.target.value, email_verified: false })); };
  const missing = REQUIRED_JOIN_FIELDS.filter((k) => !(form[k] || "").toString().trim());
  const reqCls = (k) => (!(form[k] || "").toString().trim() ? "border-red-400 focus-visible:ring-red-400" : "");

  const sendCode = async () => {
    if (!(form.email || "").trim()) { toast.error("Inserisci prima l'email."); return; }
    setSendingCode(true);
    try { await api.post("/partner/email/send-code", { email: form.email.trim() }); setCodeSent(true); toast.success("Codice di verifica inviato all'email indicata."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Invio codice non riuscito"); }
    finally { setSendingCode(false); }
  };
  const verifyCode = async () => {
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const r = await api.post("/partner/email/verify", { code: code.trim() });
      setForm((f) => ({ ...f, email: r.data.email || f.email, email_verified: true }));
      setCodeSent(false); setCode("");
      toast.success("Email verificata con successo.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verifica non riuscita"); }
    finally { setVerifying(false); }
  };

  const save = async () => {
    if (missing.length) { toast.error("Compila tutti i campi obbligatori."); return; }
    if (!isValidIban(form.iban)) { toast.error("L'IBAN inserito non è valido: controlla formato e cifre di controllo."); return; }
    if (!form.email_verified) { toast.error("Verifica l'email ordinaria prima di aderire."); return; }
    if (!form.accept_contract) { toast.error("Devi accettare il contratto Sedio Partner."); return; }
    setBusy(true);
    try {
      await api.post("/partner/join", form);
      toast.success("Adesione a Sedio Partner completata.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile completare l'adesione.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scroll-thin" data-testid="partner-join-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.name ? "Aggiorna dati Partner" : "Aderisci a Sedio Partner"}</DialogTitle>
          <DialogDescription>Inserisci i dati fiscali del tuo studio (gli stessi del profilo) e l'IBAN con cui riceverai le liquidazioni. I campi con <span className="text-red-500">*</span> sono obbligatori.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Ragione sociale / Denominazione studio<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-name" value={form.name} onChange={set("name")} className={`h-9 mt-1 ${reqCls("name")}`} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nome amministratore referente<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-referente" value={form.referente} onChange={set("referente")} className={`h-9 mt-1 ${reqCls("referente")}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Partita IVA<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-piva" value={form.partita_iva} onChange={set("partita_iva")} className={`h-9 mt-1 font-mono uppercase ${reqCls("partita_iva")}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Codice fiscale (facoltativo)</label>
            <Input data-testid="partner-cf" value={form.codice_fiscale} onChange={set("codice_fiscale")} className="h-9 mt-1 font-mono uppercase" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Indirizzo<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-indirizzo" value={form.indirizzo} onChange={set("indirizzo")} className={`h-9 mt-1 ${reqCls("indirizzo")}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Città<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-citta" value={form.citta} onChange={set("citta")} className={`h-9 mt-1 ${reqCls("citta")}`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">CAP<span className="text-red-500"> *</span></label>
              <Input data-testid="partner-cap" value={form.cap} onChange={set("cap")} className={`h-9 mt-1 ${reqCls("cap")}`} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Prov.<span className="text-red-500"> *</span></label>
              <Input data-testid="partner-provincia" value={form.provincia} onChange={set("provincia")} className={`h-9 mt-1 uppercase ${reqCls("provincia")}`} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">PEC<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-pec" value={form.pec} onChange={set("pec")} className={`h-9 mt-1 ${reqCls("pec")}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Telefono<span className="text-red-500"> *</span></label>
            <Input data-testid="partner-telefono" value={form.telefono} onChange={set("telefono")} className={`h-9 mt-1 ${reqCls("telefono")}`} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />Email ordinaria<span className="text-red-500"> *</span></label>
            <div className="flex items-center gap-2 mt-1">
              <div className="relative flex-1">
                <Input data-testid="partner-email" type="email" value={form.email} onChange={onEmailChange} className={`h-9 ${reqCls("email")}`} placeholder="posta@studio.it" />
              </div>
              {form.email_verified ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold shrink-0" data-testid="partner-email-verified"><CheckCircle2 className="h-4 w-4" />Verificata</span>
              ) : (
                <Button type="button" data-testid="partner-email-verify-btn" onClick={sendCode} disabled={sendingCode || !(form.email || "").trim()} variant="outline" className="h-9 text-xs shrink-0">
                  {sendingCode ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Verifica"}
                </Button>
              )}
            </div>
            {!form.email_verified && !codeSent && (
              <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-600" data-testid="partner-email-unverified">
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>L'email va verificata prima di aderire. Riceverai un codice via email.</span>
              </div>
            )}
            {codeSent && !form.email_verified && (
              <div className="mt-2 flex items-center gap-2" data-testid="partner-email-code-row">
                <Input data-testid="partner-email-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Codice a 6 cifre" className="h-9 w-40 font-mono tracking-widest" />
                <Button type="button" data-testid="partner-email-confirm-btn" onClick={verifyCode} disabled={verifying || code.length < 6} className="h-9 text-xs bg-[#007BFF] hover:bg-[#0069D9]">
                  {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Conferma codice"}
                </Button>
                <button type="button" onClick={sendCode} disabled={sendingCode} className="text-[11px] text-muted-foreground hover:text-foreground underline">Reinvia</button>
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Codice SDI (facoltativo)</label>
            <Input data-testid="partner-sdi" value={form.sdi} onChange={set("sdi")} className="h-9 mt-1 font-mono uppercase" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Regime fiscale (facoltativo)</label>
            <Input data-testid="partner-regime" value={form.regime_fiscale} onChange={set("regime_fiscale")} className="h-9 mt-1" />
          </div>
          <div className="sm:col-span-2">
            <IbanField
              label="IBAN (per la liquidazione)"
              required
              testId="partner-iban"
              value={form.iban}
              onChange={(v) => setForm((f) => ({ ...f, iban: v }))}
              requiredEmpty={!(form.iban || "").toString().trim()}
            />
          </div>
        </div>
        <div
          data-testid="partner-contract-scroll"
          onScroll={(e) => { const el = e.target; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setAtEnd(true); }}
          className="h-40 overflow-y-auto scroll-thin rounded-lg border border-border bg-secondary/40 p-3 text-[12px] leading-relaxed whitespace-pre-wrap font-mono mt-1"
        >
          {PARTNER_CONTRACT}
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" data-testid="partner-accept" disabled={!atEnd} checked={form.accept_contract}
            onChange={(e) => setForm((f) => ({ ...f, accept_contract: e.target.checked }))} className="mt-1" />
          <span className={!atEnd ? "text-muted-foreground" : ""}>
            Ho letto e accetto il contratto Sedio Partner {!atEnd && "(scorri il testo fino in fondo)"}
          </span>
        </label>
        <DialogFooter>
          {missing.length > 0 && <span className="text-xs text-muted-foreground mr-auto self-center" data-testid="partner-missing">{missing.length} campo/i obbligatori mancanti</span>}
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-md">Annulla</Button>
          <Button data-testid="partner-join-submit" onClick={save} disabled={busy} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BadgeCheck className="h-4 w-4 mr-2" />}
            {initial?.name ? "Salva" : "Aderisci"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Super Admin config ---------------- */
const SuperAdminPartner = () => {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({ essenziale: 0, premium: 20, elite: 30 });
  const [cfg, setCfg] = useState({ sedio_coin_value_eur: 1, availability_trigger: "first_payment", payout_min_coin: 1000 });
  const [payouts, setPayouts] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/partner/config").then((r) => {
      setData(r.data);
      const rt = r.data.fee_schedule?.rates || {};
      setRates({ essenziale: rt.essenziale ?? 0, premium: rt.premium ?? 20, elite: rt.elite ?? 30 });
      const c = r.data.config || {};
      setCfg({ sedio_coin_value_eur: c.sedio_coin_value_eur ?? 1, availability_trigger: c.availability_trigger || "first_payment", payout_min_coin: c.payout_min_coin ?? 1000 });
    }).catch(() => {});
    api.get("/admin/partner/payouts").then((r) => setPayouts(r.data.payouts || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setBusy("cfg");
    try { await api.put("/admin/partner/config", cfg); toast.success("Configurazione aggiornata."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };
  const saveRates = async () => {
    setBusy("rates");
    try {
      await api.put("/admin/partner/fee-schedule", {
        essenziale: Number(rates.essenziale), premium: Number(rates.premium), elite: Number(rates.elite),
      });
      toast.success("Nuovo Tariffario Partner pubblicato.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };

  const [grant, setGrant] = useState({ email: "", amount: "", reason: "Accredito manuale" });
  const grantCoins = async () => {
    const amt = Number(grant.amount);
    if (!grant.email.trim() || !amt || amt <= 0) { toast.error("Inserisci email e un importo maggiore di zero."); return; }
    setBusy("grant");
    try {
      const r = await api.post("/admin/partner/grant-coins", { email: grant.email.trim(), amount: amt, reason: grant.reason.trim() || "Accredito manuale" });
      toast.success(`Accreditati ${amt} SedioCoin a ${r.data.email}. Nuovo saldo: ${r.data.new_balance} SC.`);
      setGrant({ email: "", amount: "", reason: "Accredito manuale" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante l'accredito."); } finally { setBusy(""); }
  };

  if (!data) return null;
  return (
    <div className="bg-card border border-amber-300/60 rounded-xl p-6" data-testid="partner-superadmin">
      <div className="flex items-center gap-2 mb-4"><Crown className="h-4 w-4 text-amber-500" /><h3 className="font-head text-lg font-semibold">Configurazione Sedio Partner (Super Admin)</h3></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Coins className="h-4 w-4 text-emerald-600" />Tariffario Partner (SedioCoin per onboarding)</div>
          <div className="space-y-2">
            {["essenziale", "premium", "elite"].map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-28 text-sm capitalize">{k}</span>
                <Input type="number" data-testid={`rate-${k}`} value={rates[k]} onChange={(e) => setRates((r) => ({ ...r, [k]: e.target.value }))} className="h-9 w-28" />
                <span className="text-xs text-muted-foreground">SedioCoin</span>
              </div>
            ))}
          </div>
          <Button data-testid="save-rates" onClick={saveRates} disabled={busy === "rates"} className="mt-3 bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9">
            {busy === "rates" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Pubblica nuovo tariffario (v{data.fee_schedule?.version})
          </Button>
        </div>

        <div>
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Settings2 className="h-4 w-4 text-[#007BFF]" />Parametri</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-40 text-sm">Valore 1 SedioCoin (€)</span>
              <Input type="number" data-testid="coin-value" value={cfg.sedio_coin_value_eur} onChange={(e) => setCfg((c) => ({ ...c, sedio_coin_value_eur: e.target.value }))} className="h-9 w-28" />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-40 text-sm">Soglia liquidazione (SC)</span>
              <Input type="number" data-testid="payout-min" value={cfg.payout_min_coin} onChange={(e) => setCfg((c) => ({ ...c, payout_min_coin: e.target.value }))} className="h-9 w-28" />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-40 text-sm">Disponibilità coin</span>
              <select data-testid="avail-trigger" value={cfg.availability_trigger} onChange={(e) => setCfg((c) => ({ ...c, availability_trigger: e.target.value }))} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="first_payment">Alla conferma del 1° pagamento</option>
                <option value="onboarding">Subito, all'onboarding</option>
              </select>
            </div>
          </div>
          <Button data-testid="save-config" onClick={saveConfig} disabled={busy === "cfg"} className="mt-3 bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9">
            {busy === "cfg" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salva parametri
          </Button>
        </div>
      </div>

      <div className="border-t border-border pt-4 mt-5">
        <div className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Coins className="h-4 w-4 text-emerald-600" />Accredita SedioCoin a un account</div>
        <p className="text-xs text-muted-foreground mb-3">Accredito manuale di SedioCoin disponibili nel portafoglio del destinatario. L'operazione viene registrata nel ledger (tracciata e auditabile).</p>
        <div className="flex flex-col sm:flex-row sm:items-end gap-2 flex-wrap" data-testid="grant-coins-panel">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Email destinatario</label>
            <Input data-testid="grant-email" type="email" value={grant.email} onChange={(e) => setGrant((g) => ({ ...g, email: e.target.value }))} placeholder="es. info@retedomus.it" className="h-9 mt-1" />
          </div>
          <div className="w-full sm:w-36">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">SedioCoin</label>
            <Input data-testid="grant-amount" type="number" min="0" step="1" value={grant.amount} onChange={(e) => setGrant((g) => ({ ...g, amount: e.target.value }))} placeholder="500" className="h-9 mt-1" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Causale</label>
            <Input data-testid="grant-reason" value={grant.reason} onChange={(e) => setGrant((g) => ({ ...g, reason: e.target.value }))} placeholder="Accredito manuale" className="h-9 mt-1" />
          </div>
          <Button data-testid="grant-submit" onClick={grantCoins} disabled={busy === "grant"} className="bg-emerald-600 hover:bg-emerald-700 rounded-md h-9">
            {busy === "grant" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Coins className="h-4 w-4 mr-2" />}Accredita
          </Button>
        </div>
      </div>

      <div className="border-t border-border pt-4 mt-5">
        <div className="text-sm font-semibold mb-2">Richieste di liquidazione</div>
        {payouts.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nessuna richiesta di liquidazione.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="partner-payouts-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Importo</th><th className="py-2 pr-3">Movimenti</th><th className="py-2">Stato</th>
              </tr></thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.payout_id} className="border-b border-border/50">
                    <td className="py-2 pr-3">{fmtDate(p.requested_at)}</td>
                    <td className="py-2 pr-3 font-semibold">{eur(p.gross_amount)}</td>
                    <td className="py-2 pr-3">{(p.movement_ids || []).length}</td>
                    <td className="py-2">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Pagina ---------------- */
export default function SedioPartner() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinOpen, setJoinOpen] = useState(false);
  const [payoutConfirm, setPayoutConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/partner/me").then((r) => setMe(r.data)).catch(() => {}),
      api.get("/partner/summary").then((r) => setSummary(r.data)).catch(() => {}),
      api.get("/partner/ledger").then((r) => setLedger(r.data.movements || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // --- Tour guidato: crea movimenti SedioCoin DEMO e li elimina alla fine ---
  const demoRef = useRef(false);
  const startTourDemo = useCallback(async () => {
    try { await api.post("/partner/tour-demo", {}); demoRef.current = true; load(); }
    catch (e) { console.debug("tour demo create:", e); }
  }, [load]);
  const endTourDemo = useCallback(async () => {
    if (!demoRef.current) return;
    try { await api.delete("/partner/tour-demo"); } catch (e) { console.debug("tour demo delete:", e); }
    demoRef.current = false; load();
  }, [load]);

  const TOUR_STEPS = [
    { title: "Benvenuto in Sedio Partner 🪙", body: "Qui guadagni SedioCoin completando l'onboarding dei condomìni. Ho creato alcuni movimenti di esempio per mostrartelo — li elimino a fine guida." },
    { selector: "partner-summary", title: "I tuoi SedioCoin", body: "Il tuo saldo disponibile: sono i coin liquidabili, già al netto di quanto hai speso (es. ricerche Catasto). I coin diventano disponibili subito al completamento dell'onboarding. 1 SedioCoin = 1 €." },
    { selector: "partner-payout-card", title: "Liquidazione", body: "Quando superi la soglia minima puoi richiedere la liquidazione. Prima però devi aderire a Sedio Partner e inserire i dati fiscali (gli stessi del profilo studio) e l'IBAN." },
    { selector: "partner-ledger-card", title: "Storico movimenti", body: "Una lista unica con entrate (verde, corrispettivi da onboarding) e uscite (rosso, ricerche Catasto a 5 SedioCoin). Il saldo è la differenza tra le due." },
    { title: "Tutto qui! 🎉", body: "Elimino i movimenti di esempio. Riavvia questa guida quando vuoi dal pulsante \"Guida\" in alto." },
  ];

  const requestPayout = async () => {    setBusy(true);
    try {
      await api.post("/partner/payout", {});
      toast.success("Richiesta di liquidazione inviata.");
      setPayoutConfirm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Richiesta non riuscita.");
    } finally { setBusy(false); }
  };

  const joined = !!me?.account?.name;
  const pct = summary ? Math.min(100, (summary.coins_available / (summary.payout_min_coin || 1000)) * 100) : 0;

  return (
    <div className="fade-in" data-testid="partner-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Programma Partner</div>
            <h1 className="font-head text-4xl font-extrabold tracking-tighter flex items-center gap-2"><Coins className="h-8 w-8 text-emerald-600" />Sedio Partner</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Guadagni <b>SedioCoin</b> completando l'onboarding dei condomìni (creazione + collegamento + anagrafica). Non è un cashback: il corrispettivo dipende dal piano attivato, non dal prezzo pagato. 1 SedioCoin = 1 €.</p>
          </div>
          <PageTour storageKey="tour_sedio_partner" steps={TOUR_STEPS} onOpen={startTourDemo} onClose={endTourDemo} className="shrink-0 mt-1" />
        </div>
      </header>

      <div className="p-4 sm:p-10 space-y-6">
        {loading ? (
          <div className="bg-card border border-border rounded-xl p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* Riepilogo coin */}
            <div className="grid grid-cols-1 gap-4" data-testid="partner-summary">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">SedioCoin disponibili</div>
                  <div className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight mt-1" data-testid="coins-available">{coins(summary?.coins_available)}</div>
                  <div className="text-[12px] text-white/70 mt-1">≈ {eur(summary?.eur_available)} liquidabili · i SedioCoin diventano disponibili subito al completamento dell'onboarding</div>
                </div>
              </div>
            </div>

            {/* Liquidazione */}
            <div className="bg-card border border-border rounded-xl p-6" data-testid="partner-payout-card">
              <div className="flex items-center gap-2 mb-3"><HandCoins className="h-4 w-4 text-[#007BFF]" /><h3 className="font-head text-lg font-semibold">Liquidazione</h3></div>
              <div className="text-[12px] text-muted-foreground mb-1">Soglia minima: {coins(summary?.payout_min_coin)} · la liquidazione può essere richiesta <b>dopo 15 giorni</b> dal raggiungimento della soglia</div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-2">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} data-testid="payout-progress" />
              </div>
              {!joined ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 flex flex-wrap items-center gap-3" data-testid="partner-not-joined">
                  <Info className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-800 dark:text-amber-300 flex-1">Per ricevere le liquidazioni devi prima aderire a Sedio Partner e inserire i tuoi dati fiscali.</span>
                  <Button data-testid="partner-join-btn" onClick={() => setJoinOpen(true)} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-9"><BadgeCheck className="h-4 w-4 mr-2" />Aderisci ora</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Button data-testid="partner-payout-btn" onClick={() => setPayoutConfirm(true)} disabled={!summary?.can_request_payout}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-md h-10 px-5"><ArrowRight className="h-4 w-4 mr-2" />Richiedi liquidazione</Button>
                  <span className="text-[12px] text-muted-foreground">
                    {summary?.can_request_payout
                      ? "Hai raggiunto la soglia: puoi richiedere il bonifico."
                      : summary?.threshold_reached
                        ? `Soglia raggiunta: la liquidazione sarà disponibile dal ${summary?.payout_available_at ? new Date(summary.payout_available_at).toLocaleDateString("it-IT") : "—"} (attesa di ${summary?.payout_wait_days || 15} giorni).`
                        : `Mancano ${coins(Math.max(0, (summary?.payout_min_coin || 0) - (summary?.coins_available || 0)))} alla soglia.`}
                  </span>
                  <Button variant="outline" size="sm" data-testid="partner-edit-btn" onClick={() => setJoinOpen(true)} className="rounded-md h-8 ml-auto">Modifica dati Partner</Button>
                </div>
              )}
            </div>

            {/* Storico movimenti (unica lista: corrispettivi + spese Catasto) */}
            <div className="bg-card border border-border rounded-xl p-6" data-testid="partner-ledger-card">
              <div className="flex items-center gap-2 mb-1"><Wallet className="h-4 w-4 text-[#007BFF]" /><h3 className="font-head text-lg font-semibold">Storico movimenti</h3></div>
              <p className="text-[12px] text-muted-foreground mb-3">Entrate (corrispettivi da onboarding, in verde) e uscite (ricerche Catasto, 5 SedioCoin, in rosso) in un'unica lista. Il saldo disponibile è la differenza tra le due.</p>
              {ledger.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-secondary/40 rounded-md px-3 py-6 text-center flex flex-col items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-500" />
                  Nessun movimento ancora. Completa l'onboarding di un condominio (piano Premium o Elite) per guadagnare i primi SedioCoin.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]" data-testid="partner-ledger">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Condominio</th><th className="py-2 pr-3">Onboarding</th>
                      <th className="py-2 pr-3 text-right">SedioCoin</th><th className="py-2 pr-3 text-right">Valore</th><th className="py-2">Stato</th>
                    </tr></thead>
                    <tbody>
                      {ledger.map((m) => {
                        const isSpend = m.movement_type === "COIN_SPEND";
                        const sm = STATUS_META[m.status] || { label: m.status, tone: "bg-secondary text-foreground" };
                        return (
                          <tr key={m.id} className="border-b border-border/50" data-testid={`ledger-row-${m.id}`}>
                            <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{fmtDate(m.created_at)}</td>
                            <td className="py-2 pr-3"><div className="font-medium flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{m.building_name || (m.metadata && m.metadata.building_name) || "—"}</div></td>
                            <td className="py-2 pr-3">
                              {isSpend
                                ? <span className="inline-flex items-center gap-1 text-rose-600"><Search className="h-3.5 w-3.5" />Ricerca Catasto</span>
                                : (ONBOARDING_LABEL[m.onboarding_type] || m.onboarding_type)}
                            </td>
                            <td className={`py-2 pr-3 text-right font-semibold ${isSpend ? "text-rose-600" : "text-emerald-600"}`}>
                              {isSpend ? `− ${coins(Math.abs(m.sedio_coin))}` : `+ ${coins(m.sedio_coin)}`}
                            </td>
                            <td className="py-2 pr-3 text-right text-muted-foreground">{isSpend ? "—" : eur(m.monetary_value)}</td>
                            <td className="py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${sm.tone}`}>{sm.label}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {isSuper && <SuperAdminPartner />}

            {/* Crediti SMS (spostato qui da pagina dedicata per ridurre le pagine) */}
            <SmsCreditiSection />
          </>
        )}
      </div>

      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} initial={me?.account} onSaved={load} />

      <AlertDialog open={payoutConfirm} onOpenChange={(v) => { if (!v) setPayoutConfirm(false); }}>
        <AlertDialogContent data-testid="payout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Richiedere la liquidazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Verrà inviata una richiesta di liquidazione per <b>{coins(summary?.coins_available)}</b> (≈ {eur(summary?.eur_available)}).
              I SedioCoin disponibili passeranno in stato "in liquidazione" e verranno pagati via bonifico dallo staff.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payout-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="payout-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); requestPayout(); }} className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Sì, richiedi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
